import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api, { authApi, authApis } from "../../config/api";
import { toast } from "react-toastify";

// Add Credit
export const addCredit = createAsyncThunk(
  "credit/add",
  async (creditData, { rejectWithValue }) => {
    try {
      const response = await authApi.post("/credit/add", creditData);
      toast.success("Credit added successfully.");
      return response.data;
    } catch (error) {
      toast.error("Failed to add credit.");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// View My Credit
export const viewCredit = createAsyncThunk(
  "credit/view",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/credit/view-my-credit/");
      return response.data;
    } catch (error) {
      toast.error("Failed to view credit details.");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Transfer Credit
export const transferCredit = createAsyncThunk(
  "credit/transfer",
  async (transferData, { rejectWithValue }) => {
    try {
      const response = await authApi.post("/credit/transfer", transferData);
      toast.success("Credit transferred successfully.");
      return response.data;
    } catch (error) {
      toast.error("Failed to transfer credit.");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch Manager Configurations
export const fetchManagerConfigurations = createAsyncThunk(
  "credit/fetchManagerConfigurations",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApis.get("/manager/configurations/");
      return response.data;
    } catch (error) {
      toast.error("Failed to fetch manager configurations.");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch My Credit
export const fetchMyCredit = createAsyncThunk(
  "credit/fetchMyCredit",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApis.get("/company/credit/view-my-credit/");
      return response.data;
    } catch (error) {
      toast.error("Failed to fetch credit details.");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Create a New Credit Order
export const createCreditOrder = createAsyncThunk(
  "credit/createCreditOrder",
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await authApis.post(
        "/company/credit/order/create/",
        orderData
      );
      toast.success("Order created successfully.");
      return response.data;
    } catch (error) {
      toast.error("Failed to create credit order.");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Mark Payment Success
export const markPaymentSuccess = createAsyncThunk(
  "credit/markPaymentSuccess",
  async (paymentData, { rejectWithValue }) => {
    console.log(paymentData);

    try {
      const response = await authApis.post(
        "/company/credit/payment/success/",
        paymentData
      );
      toast.success("Payment marked as successful.");
      return response.data;
    } catch (error) {
      toast.error("Failed to mark payment as successful.");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch All Orders
export const fetchOrders = createAsyncThunk(
  "credit/fetchOrders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApis.get("/company/credit/orders/");
      return response.data;
    } catch (error) {
      toast.error("Failed to fetch orders.");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ** New API - Add My Credit **
export const addMyCredit = createAsyncThunk(
  "credit/addMyCredit",
  async (creditData, { rejectWithValue }) => {
    try {
      const response = await authApis.post(
        "/company/credit/add-my-credit/",
        creditData
      );
      toast.success("Credit added successfully.");
      return response.data;
    } catch (error) {
      toast.error("Failed to add my credit.");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ** New API - Credit Transfer **
export const creditTransfer = createAsyncThunk(
  "credit/creditTransfer",
  async (transferData, { rejectWithValue }) => {
    try {
      const response = await authApis.post(
        "/company/credit/credit-transfer/",
        transferData
      );
      toast.success("Credit transferred successfully.");
      return response.data;
    } catch (error) {
      toast.error(error.message || "Failed to transfer credit.");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial State
const initialState = {
  creditInfo: null,
  creditDetails: null,
  orders: {},
  managerConfigurations: null,
  loading: false,
  status: "idle",
  error: null,
};

// Credit Slice
const creditSlice = createSlice({
  name: "credit",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Add Credit
      .addCase(addCredit.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addCredit.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(addCredit.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // View Credit
      .addCase(viewCredit.pending, (state) => {
        state.loading = true;
      })
      .addCase(viewCredit.fulfilled, (state, action) => {
        state.loading = false;
        state.creditInfo = action.payload;
      })
      .addCase(viewCredit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Transfer Credit
      .addCase(transferCredit.pending, (state) => {
        state.status = "loading";
      })
      .addCase(transferCredit.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(transferCredit.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Fetch Manager Configurations
      .addCase(fetchManagerConfigurations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchManagerConfigurations.fulfilled, (state, action) => {
        state.loading = false;
        state.managerConfigurations = action.payload;
      })
      .addCase(fetchManagerConfigurations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch My Credit
      .addCase(fetchMyCredit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyCredit.fulfilled, (state, action) => {
        state.loading = false;
        state.creditDetails = action.payload;
      })
      .addCase(fetchMyCredit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Credit Order
      .addCase(createCreditOrder.fulfilled, (state) => {
        state.loading = false;
      })

      // Mark Payment Success
      .addCase(markPaymentSuccess.fulfilled, (state) => {
        state.loading = false;
      })

      // Fetch Orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ** New Add My Credit case **
      .addCase(addMyCredit.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addMyCredit.fulfilled, (state) => {
        state.status = "add_credit_succeeded";
      })
      .addCase(addMyCredit.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // ** New Credit Transfer case **
      .addCase(creditTransfer.pending, (state) => {
        state.status = "loading";
      })
      .addCase(creditTransfer.fulfilled, (state) => {
        state.status = "credit_transfer_succeeded";
      })
      .addCase(creditTransfer.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export default creditSlice.reducer;
